/**
 * Орган «Настройки» в шапке — модель писателя и стиль по умолчанию (docs/release-2026-09-19.md
 * §5.6). До 19.09.2026 здесь выбиралась одна модель; теперь это две настройки бренда под одной
 * подписью, а значение органа — «Opus 5 · v5» (модель · имя стиля по умолчанию).
 *
 * ИМЯ ФАЙЛА ОСТАЛОСЬ ПРЕЖНИМ намеренно. На `WriterModelSwitch.module.css` ссылаются лестница
 * подписей шапки (docs/ui-design.md §4.1, tokens.css, Shell.module.css) и спецификация партии
 * (§1: геометрия `.tip`, которую повторяют поповеры ⓘ). Переименование ради имени оставило бы
 * эти ссылки висеть — а по ним читают, как шапка сжимается.
 *
 * ПОЧЕМУ ОН ЗДЕСЬ, А НЕ В ДИАЛОГЕ ЗАПУСКА, ГДЕ МОДЕЛЬ СТОЯЛА РАНЬШЕ. В диалоге она правила
 * РОВНО ОДИН прогон — тот, который человек в этот момент запускал; темы расписания и переделки
 * шли моделью из конфига бренда. Здесь выбор значит то, что человек в него вкладывает: чем
 * сервис пишет ВСЁ у этого бренда. Стиль по умолчанию — настройка того же регистра: им пишется
 * всё, где стиль не выбран явно, и им же работает мониторинг. Выбор стиля на ОДИН прогон
 * остался в «Запустить генерацию» — это другой вопрос («чем написать этот пост»), и путать их
 * нельзя.
 *
 * ПОЧЕМУ ПО ЛЕВУЮ СТОРОНУ РАЗДЕЛИТЕЛЯ. Шапка выстроена «что с системой» → «что я делаю» →
 * «кто я» (`Shell.tsx`). Настройки описывают, как канал работает вообще, а не то, что человек
 * сейчас нажимает; справа от разделителя они читались бы как параметр кнопки.
 *
 * ПОДТВЕРЖДЕНИЕ ЗДЕСЬ ЛЕГЧЕ, ЧЕМ У РУБИЛЬНИКА, И ЭТО НАМЕРЕННО. Промах у рубильника
 * необратим — ушедший в канал пост назад не забрать. Промах здесь стоит одного поста,
 * написанного другой моделью или другим стилем, и виден в бэклоге до одобрения. Поэтому
 * «Сохранить» применяет сразу, а объяснить смену можно строкой причины, которая уезжает в
 * журнал.
 *
 * СМЕНА НЕ ТРОГАЕТ ИДУЩИЙ ПРОГОН. Сервис фиксирует модель и стиль один раз, на открытии
 * прогона (ПРАВИЛО 2.10 `llm-costs.md`: смена посреди серии — вторая запись кэша, то есть
 * оплаченный дважды пост). Поэтому подсказка говорит «со следующего прогона», а не «сейчас».
 *
 * «СОХРАНИТЬ» ШЛЁТ ТОЛЬКО ИЗМЕНЁННОЕ. Модель и стиль — два маршрута и две строки журнала.
 * Повторная запись неизменённой модели ради смены стиля оставила бы в журнале «сменил модель
 * писателя», которой никто не менял, — ровно ту запись, по которой потом ищут причину.
 *
 * ТРЕТЬЯ НАСТРОЙКА — «ВСЕ ПОСТЫ — РАСШИРЕННЫМ СООБЩЕНИЕМ» (19.09.2026, docs/release-2026-09-19.md
 * §9.3). Того же регистра: как бренд ПИШЕТ, а не что человек сейчас нажимает. Свой маршрут
 * (`POST /api/v1/transport-policy`) и своя строка журнала, та же причина и то же «Сохранить».
 * В значение органа она не входит — «Opus 5 · v5» и так на пределе ширины шапки, — но
 * отклонение от умолчания (политику ВЫКЛЮЧИЛИ) видно рамкой, а состояние и автор — подсказкой.
 * Действует на версии, сохранённые после смены: уже замороженная уходит тем способом, который
 * в неё подписан.
 */

import { limitValue, SEND_MESSAGE_MAX_CODEPOINTS } from '@smm/contracts';
import {
  type SetTransportPolicyBody,
  TRANSPORT_RICH_ALWAYS_DEFAULT,
  type TransportPolicy,
  TransportPolicySchema,
  WRITER_SELECTABLE_MODELS,
  type WriterSelectableModel,
} from '@smm/contracts/schemas';
import { type JSX, useId, useState } from 'react';
import { Link } from 'wouter';
import { ApiRequestError, post } from '../api/client.ts';
import type { StyleDefault, WriterModelOverride } from '../api/types.ts';
import { useResource } from '../api/useResource.ts';
import { shortMsk } from './format.ts';
import { InfoPopover } from './InfoPopover.tsx';
import { IconSliders } from './icons.tsx';
import { Modal } from './Modal.tsx';
import {
  parseStyleDefault,
  parseStyleVersions,
  pickableStyles,
  styleWithKind,
} from './styleVersions.ts';
import styles from './WriterModelSwitch.module.css';

/**
 * Подписи моделей. Список приходит из контрактов и здесь НЕ дописывается: свой перечень в
 * интерфейсе стал бы вторым источником правды о том, чем можно писать, и разошёлся бы с
 * прайсом и с сервисом молча. Здесь только перевод идентификатора в то, как модель называют
 * люди.
 */
const MODEL_LABEL: Readonly<Record<WriterSelectableModel, string>> = {
  'claude-opus-5-5': 'Opus 5.5',
  'claude-opus-5': 'Opus 5',
  'claude-opus-4-8': 'Opus 4.8',
  'claude-opus-4-7': 'Opus 4.7',
};

/** Незнакомый код показывается как есть: молчать о нём хуже, чем показать строку. */
export function modelLabel(code: string): string {
  return MODEL_LABEL[code as WriterSelectableModel] ?? code;
}

function isSelectable(code: string): code is WriterSelectableModel {
  return (WRITER_SELECTABLE_MODELS as readonly string[]).includes(code);
}

const errorText = (e: unknown): string =>
  e instanceof ApiRequestError || e instanceof Error ? e.message : String(e);

/**
 * Ответ `GET /api/v1/transport-policy` — схемой: шапка живёт на каждом экране, и чужой ответ
 * (бэкенд без второго релиза партии, HTML прокси) обязан стать «не загрузилась», а не
 * исключением.
 */
export function parseTransportPolicy(raw: unknown): TransportPolicy | null {
  const r = TransportPolicySchema.safeParse(raw);
  return r.success ? r.data : null;
}

const capitalized = (text: string): string => `${text.charAt(0).toUpperCase()}${text.slice(1)}`;

/** «Да» / «Нет» словами подсказки; умолчание подписано на своём чипе, как у модели. */
const RICH_WORD = (on: boolean): string => (on ? 'да' : 'нет');

export interface WriterModelSwitchProps {
  readonly brand: string;
  /**
   * Почему генерации у бренда нет (`agentsOffReason`, «генерация для … ещё не подключена»);
   * `null` или не задано — сервис генерации бренд обслуживает. У бренда без писателя модели
   * по умолчанию нет вовсе (`brand_default: null`), и «работает модель по умолчанию» было бы
   * неправдой: значение органа — прочерк, причина — в подсказке. Сам орган не гаснет:
   * политика отправки действует и на посты, написанные людьми.
   */
  readonly generationOff?: string | null | undefined;
  /** `undefined`, пока не загружено или запрос провалился. */
  readonly model: WriterModelOverride | undefined;
  readonly modelFailed: boolean;
  /**
   * Ответ `GET /api/v1/style-default` как пришёл; `undefined`, пока не загружено. Разбирается
   * здесь схемой: шапка живёт на каждом экране, и чужой ответ обязан стать прочерком, а не
   * исключением.
   */
  readonly styleDefault: unknown;
  readonly styleFailed: boolean;
  /**
   * Ответ `GET /api/v1/transport-policy` как пришёл; `undefined`, пока не загружено.
   * Разбирается здесь схемой (`parseTransportPolicy`) по той же причине, что стиль.
   */
  readonly transport: unknown;
  readonly transportFailed: boolean;
  /** Что-то сохранено: шапка перечитывает все настройки. */
  readonly onChanged: () => void;
}

export function WriterModelSwitch({
  brand,
  generationOff = null,
  model,
  modelFailed,
  styleDefault,
  styleFailed,
  transport,
  transportFailed,
  onChanged,
}: WriterModelSwitchProps): JSX.Element | null {
  const tipId = useId();
  const [editing, setEditing] = useState(false);

  // Пока хотя бы одна настройка в пути, органа нет: показать «Opus 5 · —» до ответа сервера
  // значит утверждать про стиль то, чего никто не подтверждал, — а через секунду передумать.
  // Политика отправки ждётся так же: от неё зависит рамка органа, и рамка, появившаяся через
  // секунду после органа, читалась бы как событие, а не как состояние.
  if (
    (model === undefined && !modelFailed) ||
    (styleDefault === undefined && !styleFailed) ||
    (transport === undefined && !transportFailed)
  ) {
    return null;
  }
  const style = parseStyleDefault(styleDefault);
  const policy = parseTransportPolicy(transport);

  if (model === undefined && style === null) {
    return (
      <div className={styles['wrap']}>
        <span className={styles['btn']} title="Настройки неизвестны">
          <span className={styles['glyph']}>
            <IconSliders />
          </span>
          <span className={styles['cap']}>Настройки</span>
          <span className={styles['name']}>—</span>
        </span>
        <span className={styles['tip']} role="tooltip">
          <b>Настройки неизвестны</b>
          <span>
            Модель писателя и стиль по умолчанию: состояние не загрузилось. Обновите страницу.
          </span>
        </span>
      </div>
    );
  }

  // Что реально поедет в следующий прогон: отклонение, если оно есть, иначе конфиг бренда.
  const effectiveModel = model === undefined ? undefined : (model.model ?? model.brand_default);
  // Без писателя «по умолчанию» ничего не значит: умолчание — модель секции `generation`,
  // которой нет. Выставленное отклонение показывается — оно начнёт действовать с подключением.
  const modelText =
    effectiveModel === undefined || (effectiveModel === null && generationOff !== null)
      ? '—'
      : effectiveModel === null
        ? 'по умолчанию'
        : modelLabel(effectiveModel);
  const styleText = style === null ? '—' : style.effective.name;
  // Отклонение от умолчаний — любая из трёх: модель не из конфига, стиль не релиз или политика
  // отправки не та, что по умолчанию (её выключили).
  const overridden =
    (model !== undefined && model.model !== null) ||
    (style !== null && style.effective.kind === 'custom') ||
    (policy !== null && policy.rich_always !== TRANSPORT_RICH_ALWAYS_DEFAULT);
  const value = `${modelText} · ${styleText}`;

  return (
    <div className={styles['wrap']}>
      <button
        type="button"
        aria-describedby={tipId}
        aria-label={`Настройки: модель ${modelText}, стиль ${styleText}`}
        className={overridden ? `${styles['btn']} ${styles['on']}` : styles['btn']}
        onClick={() => {
          setEditing(true);
        }}
      >
        {/* Значок виден только на телефоне, где не помещаются ни подпись, ни значение: орган
            обязан остаться видимым (docs/ui-design.md §4.1), а имя держит `aria-label`. */}
        <span className={styles['glyph']}>
          <IconSliders />
        </span>
        <span className={styles['cap']}>Настройки</span>
        <span className={styles['name']}>{value}</span>
      </button>

      <span className={styles['tip']} role="tooltip" id={tipId}>
        <b>Настройки</b>
        <span>{`Модель писателя — ${modelText}.`}</span>
        {generationOff !== null ? (
          <span className={styles['tipNote']}>
            {`${capitalized(generationOff)}: модель и стиль начнут действовать, когда сервис генерации подключат.`}
          </span>
        ) : model === undefined ? (
          <span className={styles['tipNote']}>Модель не загрузилась.</span>
        ) : (
          <ModelTipNotes value={model} />
        )}
        <span>{`Стиль по умолчанию — ${style === null ? '—' : styleWithKind(style.effective)}.`}</span>
        {style === null ? (
          <span className={styles['tipNote']}>Стиль не загрузился.</span>
        ) : (
          <StyleTipNote value={style} />
        )}
        <span>
          {`Все посты — расширенным сообщением: ${policy === null ? '—' : RICH_WORD(policy.rich_always)}.`}
        </span>
        {policy === null ? (
          <span className={styles['tipNote']}>Политика отправки не загрузилась.</span>
        ) : (
          <PolicyTipNote value={policy} />
        )}
        <span className={styles['tipNote']}>
          Модель — на всё, что пишет сервис: прогоны по кнопке, темы расписания и переделки. Стиль —
          там, где его не выбрали явно. Смена применяется со следующего прогона — начатый пост
          дописывается тем, чем начат.
        </span>
      </span>

      {editing ? (
        <SettingsDialog
          brand={brand}
          model={model}
          style={style}
          policy={policy}
          policyFailed={transportFailed}
          onChanged={onChanged}
          onClose={() => {
            setEditing(false);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Кто и когда поставил политику. Три случая, и путать их нельзя:
 *   · строки нет (`source: 'default'`) — действует умолчание, и приписывать его человеку нельзя;
 *   · строка без автора — её записала выкладка (`transport_policy` AGI Boardroom, build spec
 *     2026-11-02 §9: в журнале автор `system:deploy`, в строке — `changed_by_user_id NULL`).
 *     «Не переключали — по умолчанию включено» здесь было бы ложью дважды: политику поставили, и
 *     стоит она, возможно, в «нет»;
 *   · строка с автором — решение человека: кто, когда, зачем.
 */
function PolicyTipNote({ value }: { value: TransportPolicy }): JSX.Element {
  if (value.source === 'default') {
    return (
      <span className={styles['tipNote']}>
        {`Не переключали — по умолчанию ${TRANSPORT_RICH_ALWAYS_DEFAULT ? 'включено' : 'выключено'}.`}
      </span>
    );
  }
  const who = value.changed_by_login ?? 'установлено выкладкой';
  return (
    <span className={styles['tipNote']}>
      {who}
      {value.updated_at === null ? '' : ` · ${shortMsk(value.updated_at)}`}
      {value.note === null ? '' : ` · ${value.note}`}
    </span>
  );
}

function ModelTipNotes({ value }: { value: WriterModelOverride }): JSX.Element {
  return (
    <>
      {value.model !== null ? null : (
        <span className={styles['tipNote']}>
          {value.brand_default === null
            ? 'Отклонения нет — работает модель по умолчанию.'
            : `Отклонения нет — работает модель по умолчанию (${modelLabel(value.brand_default)}).`}
        </span>
      )}
      {value.changed_at === null || value.changed_by_login === null ? (
        // Умолчание — конфиг бренда; приписывать его человеку нельзя.
        <span className={styles['tipNote']}>Не переключалась.</span>
      ) : (
        <span className={styles['tipNote']}>
          {`${value.changed_by_login} · ${shortMsk(value.changed_at)}`}
          {value.note === null ? '' : ` · ${value.note}`}
        </span>
      )}
    </>
  );
}

function StyleTipNote({ value }: { value: StyleDefault }): JSX.Element {
  if (value.version_id === null || value.changed_at === null || value.changed_by_login === null) {
    // Строки выбора нет — работает релиз, и приписывать его человеку нельзя.
    return <span className={styles['tipNote']}>Не выбирали — работает релиз.</span>;
  }
  return (
    <span className={styles['tipNote']}>
      {`${value.changed_by_login} · ${shortMsk(value.changed_at)}`}
      {value.note === null ? '' : ` · ${value.note}`}
    </span>
  );
}

/**
 * Диалог «Настройки»: три группы чипов, ссылка на редактор стилей и одна причина на все
 * смены. Базовые значения — то, что было на открытии, и после каждого удачного сохранения
 * они сдвигаются: если один из маршрутов отказал, повторное «Сохранить» пошлёт только
 * несохранённое, а не запишет удачное второй раз.
 */
function SettingsDialog({
  brand,
  model,
  style,
  policy,
  policyFailed,
  onChanged,
  onClose,
}: {
  readonly brand: string;
  readonly model: WriterModelOverride | undefined;
  readonly style: StyleDefault | null;
  readonly policy: TransportPolicy | null;
  readonly policyFailed: boolean;
  readonly onChanged: () => void;
  readonly onClose: () => void;
}): JSX.Element {
  const noteId = useId();
  const versions = useResource<unknown>(`/api/v1/styles?brand=${encodeURIComponent(brand)}`);
  const list = parseStyleVersions(versions.data);
  const listBroken = versions.error !== undefined || (versions.data !== undefined && list === null);

  const startModel = model?.model ?? null;
  // Релиз — это `null`, как «(дефолт)» у модели: выбор релиза снимает выбор, а не закрепляет
  // строку релиза. Иначе после выкладки нового релиза умолчание осталось бы на старой строке.
  const startStyle =
    style === null || style.effective.kind === 'release' ? null : style.effective.id;

  const startRich = policy?.rich_always ?? TRANSPORT_RICH_ALWAYS_DEFAULT;

  const [modelBase, setModelBase] = useState<string | null>(startModel);
  const [modelChoice, setModelChoice] = useState<string | null>(startModel);
  const [styleBase, setStyleBase] = useState<string | null>(startStyle);
  const [styleChoice, setStyleChoice] = useState<string | null>(startStyle);
  const [richBase, setRichBase] = useState<boolean>(startRich);
  const [richChoice, setRichChoice] = useState<boolean>(startRich);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<readonly string[]>([]);

  const modelDirty = model !== undefined && modelChoice !== modelBase;
  const styleDirty = style !== null && styleChoice !== styleBase;
  const richDirty = policy !== null && richChoice !== richBase;
  const blocked = busy || (!modelDirty && !styleDirty && !richDirty);

  const save = (): void => {
    const why = note.trim() === '' ? null : note.trim();
    const jobs: Promise<string | null>[] = [];
    if (modelDirty) {
      const next: WriterSelectableModel | null =
        modelChoice !== null && isSelectable(modelChoice) ? modelChoice : null;
      jobs.push(
        post('/api/v1/writer-model', { brand, model: next, note: why }).then(
          () => {
            setModelBase(modelChoice);
            return null;
          },
          (e: unknown) => `Модель писателя: ${errorText(e)}`,
        ),
      );
    }
    if (styleDirty) {
      jobs.push(
        post('/api/v1/style-default', { brand, version_id: styleChoice, note: why }).then(
          () => {
            setStyleBase(styleChoice);
            return null;
          },
          (e: unknown) => `Стиль по умолчанию: ${errorText(e)}`,
        ),
      );
    }
    if (richDirty) {
      const body: SetTransportPolicyBody = { brand, rich_always: richChoice, note: why };
      jobs.push(
        post('/api/v1/transport-policy', body).then(
          () => {
            setRichBase(richChoice);
            return null;
          },
          (e: unknown) => `Все посты — расширенным сообщением: ${errorText(e)}`,
        ),
      );
    }
    if (jobs.length === 0) return;
    setBusy(true);
    setErrors([]);
    Promise.all(jobs).then((results) => {
      setBusy(false);
      const failed = results.filter((r): r is string => r !== null);
      // Сохранилось хоть что-то — шапка перечитывает настройки сразу, а не после второй
      // попытки: иначе она показывала бы прежнее значение того, что уже поменяно.
      if (failed.length < results.length) onChanged();
      if (failed.length === 0) {
        onClose();
        return;
      }
      setErrors(failed);
    });
  };

  const effectiveStyleId = style?.effective.id ?? null;
  const visible = list === null ? [] : pickableStyles(list.versions, effectiveStyleId);
  const brandDefault = model?.brand_default ?? null;

  return (
    <Modal title="Настройки" width={560} onClose={onClose}>
      <fieldset className={styles['field']}>
        <legend className="lbl">Модель писателя</legend>
        {model === undefined ? (
          <div className="val val-err">
            <span>Модель писателя не загрузилась — менять её сейчас нечем.</span>
          </div>
        ) : (
          <div className={styles['chips']}>
            {/*
              УМОЛЧАНИЕ — ЭТО МОДЕЛЬ, А НЕ ОТДЕЛЬНЫЙ ЧИП (правка 10.09.2026). Модель конфига
              бренда подписана «(дефолт)» прямо на своём чипе, и нажатие на неё отправляет
              `null` — снимает отклонение, а не закрепляет имя: иначе правка конфига перестала
              бы что-либо менять.
            */}
            {brandDefault !== null && isSelectable(brandDefault) ? null : (
              // Модель конфига вне списка выбора (или конфиг её не задал): у умолчания нет
              // своего чипа среди моделей, и снять отклонение иначе было бы нечем.
              <button
                type="button"
                className={modelChoice === null ? 'fchip on' : 'fchip'}
                aria-pressed={modelChoice === null}
                onClick={() => {
                  setModelChoice(null);
                }}
              >
                {brandDefault === null ? 'Дефолт' : `${modelLabel(brandDefault)} (дефолт)`}
              </button>
            )}
            {WRITER_SELECTABLE_MODELS.map((m) => {
              const isDefault = m === brandDefault;
              const on = modelChoice === m || (isDefault && modelChoice === null);
              return (
                <button
                  key={m}
                  type="button"
                  className={on ? 'fchip on' : 'fchip'}
                  aria-pressed={on}
                  onClick={() => {
                    setModelChoice(isDefault ? null : m);
                  }}
                >
                  {isDefault ? `${MODEL_LABEL[m]} (дефолт)` : MODEL_LABEL[m]}
                </button>
              );
            })}
            <InfoPopover label="Что значит модель писателя">
              <span>
                Выбранной моделью сервис пишет всё: прогоны по кнопке, темы расписания и переделки.
              </span>
              <span>
                Все три модели одинаковы по контракту запроса и по цене — выбор про то, чем
                сравнивать качество, а не про деньги.
              </span>
              <span>Смена применяется со следующего прогона.</span>
            </InfoPopover>
          </div>
        )}
      </fieldset>

      <fieldset className={styles['field']}>
        <legend className="lbl">Стиль по умолчанию</legend>
        {style === null ? (
          <div className="val val-err">
            <span>Стиль по умолчанию не загрузился — менять его сейчас нечем.</span>
          </div>
        ) : listBroken ? (
          <div className="val val-err">
            <span>
              {`Версии стиля не загрузились${versions.error === undefined ? '' : `: ${versions.error.message}`}.`}
            </span>
          </div>
        ) : list === null ? null : (
          <div className={styles['chips']}>
            {visible.map((v) => {
              const isRelease = v.kind === 'release';
              const on = isRelease ? styleChoice === null : styleChoice === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  className={on ? 'fchip on' : 'fchip'}
                  aria-pressed={on}
                  onClick={() => {
                    setStyleChoice(isRelease ? null : v.id);
                  }}
                >
                  {styleWithKind(v)}
                </button>
              );
            })}
            <InfoPopover label="Что значит стиль по умолчанию">
              <span>
                Стилем по умолчанию пишутся прогоны, где стиль не выбран явно: кнопка «Запустить
                генерацию» и темы расписания. Им же работает мониторинг.
              </span>
              <span>Переделка пишет стилем той версии, которую переделывает.</span>
              <span>
                Релиз меняется только выкладкой; копии правятся в редакторе стилей. Смена
                применяется со следующего прогона.
              </span>
            </InfoPopover>
          </div>
        )}
        <Link
          href={`/b/${encodeURIComponent(brand)}/styles`}
          className={styles['editorLink']}
          onClick={onClose}
        >
          Редактор стилей →
        </Link>
      </fieldset>

      <fieldset className={styles['field']}>
        <legend className="lbl">Все посты — расширенным сообщением</legend>
        {policy === null ? (
          <div className="val val-err">
            <span>
              {policyFailed
                ? 'Политика отправки не загрузилась — менять её сейчас нечем.'
                : 'Политика отправки ещё не загрузилась.'}
            </span>
          </div>
        ) : (
          <div className={styles['chips']}>
            {/* Умолчание подписано «(дефолт)» на своём чипе, как модель конфига бренда: без
                строки в `transport_policy` действует именно оно. */}
            {[true, false].map((on) => (
              <button
                key={String(on)}
                type="button"
                className={richChoice === on ? 'fchip on' : 'fchip'}
                aria-pressed={richChoice === on}
                onClick={() => {
                  setRichChoice(on);
                }}
              >
                {`${on ? 'Да' : 'Нет'}${on === TRANSPORT_RICH_ALWAYS_DEFAULT ? ' (дефолт)' : ''}`}
              </button>
            ))}
            <InfoPopover label="Что значит «все посты — расширенным сообщением»">
              <span>
                Включено — каждый новый пост уходит расширенным сообщением, даже короткий и без
                картинки. У расширенного сообщения нет карточки превью ссылки.
              </span>
              <span>
                Уже сохранённые версии уходят тем способом, который в них подписан. Версию,
                сохранённую обычным сообщением, переводит кнопка «Перевести в расширенное» у превью
                поста.
              </span>
              <span>
                {`Выключение возвращает обычные сообщения с карточкой для коротких постов. Посты с картинкой, файлом или длиннее ${String(limitValue(SEND_MESSAGE_MAX_CODEPOINTS))} знаков уходят расширенным сообщением при любой настройке.`}
              </span>
              <span>
                Смена действует на версии, сохранённые после неё: новые черновики и правки.
              </span>
            </InfoPopover>
          </div>
        )}
      </fieldset>

      <div>
        <label className="lbl" htmlFor={noteId}>
          Причина
        </label>
        <input
          id={noteId}
          className="inp"
          value={note}
          maxLength={500}
          placeholder="зачем сменили — видно в журнале"
          onChange={(e) => {
            setNote(e.target.value);
          }}
        />
      </div>

      {errors.length === 0 ? null : (
        <div className="val val-err" role="alert">
          <span className={styles['errors']}>
            {errors.map((e) => (
              <span key={e}>{e}</span>
            ))}
          </span>
        </div>
      )}

      <div className={styles['actions']}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
          Отмена
        </button>
        <button
          type="button"
          className={blocked ? 'btn btn-sm btn-dis' : 'btn btn-pri btn-sm'}
          disabled={blocked}
          onClick={save}
        >
          Сохранить
        </button>
      </div>
    </Modal>
  );
}
